import React, { useState } from 'react';
import TabContext from '@mui/lab/TabContext';

import { useSelector } from 'react-redux';
import { Command, selectCommandByNodeId, State } from '@store/index';
import { TabList, TabPanel } from '@mui/lab';
import { Alert, Divider, Stack, Tab } from '@mui/material';
import { NodeInputExpression, NodeSidebar, NodeSidebarTab, NodeSidebarTabsContainer } from '@common/Node/NodeSidebar';
import { NodeField, NodeFieldType } from '../../types';
import { Button } from '@common/Button';

/**
 * This is a workaround right now. Some nodes have fields that are hidden.
 * An example is the Answer command has transcription, but it's hidden because it
 * doesn't work as expected. This method checks for all existing hidden fields
 * and is used to hide them from the sidebar.
 * @param field The field to check
 * @returns True if the field should be hidden from the JumpTo node sidebar.
 */

const hiddenField = (field: NodeField, targetCommand: Command) => {
  if (field.id === 'name') return true;

  if (targetCommand) {
    const isAnswerCommand = targetCommand.config.command_id === 'call.answered';
    const isCompletionCommand = targetCommand.config.command_id === 'llm.completion';

    if (isAnswerCommand) {
      if (field.id === 'transcription') return true;
    }

    if (isCompletionCommand) {
      const toolAttached = targetCommand?.connections?.some((connection) => connection.target_anchor.label === 'Tools');
      if (toolAttached && field.id === 'model') return true;

      if (targetCommand?.state?.json_mode?.enabled === true && field.id === 'model') return true;
      if (targetCommand?.state?.json_mode?.enabled === true && field.id === 'model') return true;
      if (targetCommand?.state?.json_mode?.enabled === true && field.id === 'system') return true;
      if (targetCommand?.state?.json_mode?.enabled === true && field.id === 'user_id') return true;
    }

    return false;
  }

  return false;
};

/**
 * Used to filter fields that are compatible with the JumpTo node
 * @param targetCommand The target command to retrieve fields from
 * @returns The fields that are compatible
 */

export const filterJumpFields = (targetCommand: Command) => {
  const fields = targetCommand.fields || [];
  const state = targetCommand.state;

  // these fields do not have tab = 1 but should be included
  const additionalFields = ['user_id', 'session_id', 'message_storage'];

  return fields.filter((field) => {
    if (hiddenField(field, targetCommand)) return false;
    if (additionalFields.includes(field.id)) return true;

    if (field.id === 'node_id' || field.id === 'max_jumps') return false;

    const isFirstTab = field.tab === '1';
    const isInputExpression = field.type === NodeFieldType.InputExpression;
    const isOptionExpression = field.type === NodeFieldType.OptionExpression;
    const isInput = field.type === NodeFieldType.Input;
    const isOption = field.type === NodeFieldType.Option;
    const isNumberExpression = field.type === NodeFieldType.NumberExpression;
    const stateExists = state.hasOwnProperty(field.id);

    const isValidType = isInputExpression || isOptionExpression || isInput || isOption || isNumberExpression;
    if (isFirstTab && isValidType && stateExists) return true;
    return false;
  });
};

export const JumpToTabs: React.FC = () => {
  const [tab, setTab] = useState('1');
  const [showJumpWarning, setShowJumpWarning] = useState(!Boolean(localStorage.getItem('hideJumpWarning')));

  const commands = useSelector((state: State) => state.tabs.active.commands);
  const command = useSelector((state: State) => selectCommandByNodeId(state, state.tabs.active.selectedNode));
  if (!command) return null;

  if (command?.config?.version === '1.0') {
    return (
      <NodeSidebar>
        <NodeSidebarTab>
          <Alert severity='error'>
            This was the Telnyx only version of Jump To. This node has been released to the public. Please remove this
            node and recreate it to use the latest version.
          </Alert>
        </NodeSidebarTab>
      </NodeSidebar>
    );
  }

  const fields = command.fields;
  const state = command.state;

  const jumpFields = fields.filter((field) => field.id === 'node_id' || field.id === 'max_jumps');
  const targetNodeId = state?.node_id;
  const targetCommand = commands.find((command) => command.node.id === targetNodeId);

  const targetCommandFields = targetCommand ? filterJumpFields(targetCommand) : [];
  const hasInputs = targetCommandFields.length > 0;

  const hideJumpWarning = () => {
    setShowJumpWarning(false);
    localStorage.setItem('hideJumpWarning', 'true');
  };

  return (
    <NodeSidebar>
      <TabContext value={tab}>
        <NodeSidebarTabsContainer color={command.config.background}>
          <TabList onChange={(_, newTab) => setTab(newTab)}>
            <Tab disableRipple label='Node' value='1' />
          </TabList>
        </NodeSidebarTabsContainer>
        <TabPanel value='1'>
          <NodeSidebarTab>
            {jumpFields.map((field) => (
              <NodeInputExpression key={field.id} command={command} field={field} />
            ))}
            {targetNodeId && <Divider />}
            {hasInputs
              ? showJumpWarning && (
                  <Stack spacing={1}>
                    <Alert severity='info'>
                      The target values for this node have been automatically filled in. Make sure to update any dynamic
                      variables to new values or connect a data line to continue using them. Any inputs not listed here
                      will be taken from the target jump node.
                    </Alert>
                    <Button small customColor='blue' onClick={hideJumpWarning}>
                      Dont Show Again
                    </Button>
                    <Divider sx={{ paddingTop: '0.75rem !important' }} />
                  </Stack>
                )
              : targetNodeId && <Alert severity='info'>The target node does not have any inputs.</Alert>}
            {targetCommandFields.map((field) => (
              <NodeInputExpression key={field.id} command={command} field={field} />
            ))}
          </NodeSidebarTab>
        </TabPanel>
      </TabContext>
    </NodeSidebar>
  );
};
